/**
 * File viewer modal — image / pdf / video / audio / text / Word document /
 * fallback. Ported from the legacy FilePreview (deleted in 8ff1ac2b), adapted
 * to the Vault Documents data shape. Shared by Browse, Search and the Desktop
 * (narrow screens and Quick Look; wide Desktops use viewer windows).
 *
 * The header offers the arches that can open the file for real ("Open in
 * Image Editor"), from the same registry the Desktop uses
 * (lib/file-openers.ts).
 */
import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Download as DownloadIcon,
  File as FileIcon,
  Loader2,
  RotateCw,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { api, type VaultDocumentDto } from '@/lib/api';
import { formatBytes } from '@/components/files/file-dialog/format';
import { previewTypeFor, type PreviewType } from '../../lib/file-display';
import type { FileApp } from '../../lib/file-openers';
import { useFileOpeners } from '../../hooks/useFileOpeners';
import { FilePreviewPane } from './FilePreviewPane';

export interface FileViewerProps {
  document: VaultDocumentDto | null;
  onClose: () => void;
  onDownload: (document: VaultDocumentDto) => void;
  /** Open the file in an arche ("Open in Image Editor"). */
  onOpenInApp?: (document: VaultDocumentDto, app: FileApp) => void;
}

export function FileViewer({ document: doc, onClose, onDownload, onOpenInApp }: FileViewerProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const { openerFor } = useFileOpeners();

  const previewType = previewTypeFor(doc?.mimeType ?? null);

  // Fetch a signed URL (and text content for text previews) whenever
  // the viewer opens on a different doc. Reset zoom/rotation so the
  // user starts from a known state.
  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUrl(null);
    setTextContent(null);
    setZoom(1);
    setRotation(0);
    (async () => {
      try {
        // The preview renders in place (the PDF frame needs an `inline` URL —
        // an attachment one would download instead); the server downgrades
        // any type that could run script. Download keeps its own attachment
        // URL (`onDownload`).
        const res = await api.getVaultDocumentDownloadUrl(doc.documentId, {
          inline: previewType !== 'unsupported',
        });
        if (cancelled) return;
        setUrl(res.url);
        if (previewType === 'text') {
          // Fetch up to ~100KB and render as monospace. Truncation
          // matches the legacy preview behavior. Network failure on
          // this fetch falls through to the unsupported branch via
          // the catch below.
          const textRes = await fetch(res.url);
          if (cancelled) return;
          const raw = await textRes.text();
          setTextContent(raw.slice(0, 100_000));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load preview');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doc, previewType]);

  // Esc closes; +/- zoom; 0 resets. Only meaningful for image preview
  // but harmless otherwise (the keys don't conflict with native
  // controls on video/audio/iframe).
  useEffect(() => {
    if (!doc) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (previewType === 'image') {
        if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(z + 0.25, 5));
        else if (e.key === '-') setZoom((z) => Math.max(z - 0.25, 0.25));
        else if (e.key === '0') {
          setZoom(1);
          setRotation(0);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [doc, onClose, previewType]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!doc) return;
    const prev = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.document.body.style.overflow = prev;
    };
  }, [doc]);

  if (!doc) return null;

  const showImageControls = previewType === 'image';
  const opener = openerFor(doc);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <h2 className="truncate text-lg font-medium text-foreground">{doc.name}</h2>
          <span className="text-sm text-muted-foreground">{formatBytes(doc.sizeBytes)}</span>
        </div>
        <div className="flex items-center gap-2">
          {showImageControls && (
            <>
              <button
                type="button"
                aria-label="Zoom out"
                title="Zoom out"
                onClick={() => setZoom((z) => Math.max(z - 0.25, 0.25))}
                className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="w-12 text-center text-sm text-muted-foreground">
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                aria-label="Zoom in"
                title="Zoom in"
                onClick={() => setZoom((z) => Math.min(z + 0.25, 5))}
                className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Rotate"
                title="Rotate"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              >
                <RotateCw className="h-4 w-4" />
              </button>
              <div className="mx-1 h-6 w-px bg-border" />
            </>
          )}
          {onOpenInApp &&
            opener.apps.map((app) => (
              <button
                key={app.archeId}
                type="button"
                onClick={() => onOpenInApp(doc, app)}
                className="rounded px-2 py-1 text-sm text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              >
                Open in {app.label}
              </button>
            ))}
          <button
            type="button"
            aria-label="Download"
            title="Download"
            onClick={() => onDownload(doc)}
            className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
          >
            <DownloadIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Close"
            title="Close (Esc)"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 items-center justify-center overflow-hidden p-4">
        {opener.preview === 'document' ? (
          // A Word document: converted and sanitised by the shared pane.
          <FilePreviewPane doc={doc} preview="document" className="rounded" />
        ) : loading ? (
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        ) : error ? (
          <UnsupportedPreviewFallback
            name={doc.name}
            error={error}
            onDownload={() => onDownload(doc)}
          />
        ) : url ? (
          <PreviewContent
            type={previewType}
            url={url}
            name={doc.name}
            textContent={textContent}
            zoom={zoom}
            rotation={rotation}
            onDownload={() => onDownload(doc)}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Image preview with zoom + pan. Once zoomed past fit, the image becomes
 * draggable (and wheel/trackpad scrollable) so the user can move around to see
 * the parts that overflow the viewport. Offsets are clamped so the image can't
 * be dragged fully out of view, and recenter when zoomed back to fit. The
 * parent container stays `overflow-hidden` — we pan within it rather than grow
 * a scroll area, which keeps the centered/rotated transforms simple.
 */
function ImagePreview({
  url,
  name,
  zoom,
  rotation,
}: {
  url: string;
  name: string;
  zoom: number;
  rotation: number;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  const canPan = zoom > 1;

  // Clamp a candidate offset so the image edges stay within the viewport
  // (transform-origin is center). Accounts for 90°/270° rotation swapping the
  // visual width/height. Returns {0,0}-bounded values when the image fits.
  const clampOffset = (x: number, y: number) => {
    const img = imgRef.current;
    const container = img?.parentElement;
    if (!img || !container) return { x, y };
    const rotated = rotation % 180 !== 0;
    const visualW = (rotated ? img.clientHeight : img.clientWidth) * zoom;
    const visualH = (rotated ? img.clientWidth : img.clientHeight) * zoom;
    const maxX = Math.max(0, (visualW - container.clientWidth) / 2);
    const maxY = Math.max(0, (visualH - container.clientHeight) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  };

  // Re-clamp (and recenter when it fits again) whenever zoom or rotation change.
  // (clampOffset reads the latest zoom/rotation via closure; depending on those
  // two values is the intended trigger.)
  useEffect(() => {
    setOffset((o) => clampOffset(o.x, o.y));
  }, [zoom, rotation]);

  return (
    <img
      ref={imgRef}
      src={url}
      alt={name}
      draggable={false}
      onPointerDown={(e) => {
        if (!canPan) return;
        setDragging(true);
        dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!dragging) return;
        setOffset(
          clampOffset(
            dragStart.current.ox + (e.clientX - dragStart.current.x),
            dragStart.current.oy + (e.clientY - dragStart.current.y),
          ),
        );
      }}
      onPointerUp={(e) => {
        setDragging(false);
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          /* pointer may already be released */
        }
      }}
      onPointerCancel={() => setDragging(false)}
      onWheel={(e) => {
        if (!canPan) return;
        setOffset((o) => clampOffset(o.x - e.deltaX, o.y - e.deltaY));
      }}
      className="max-h-full max-w-full select-none object-contain"
      style={{
        transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
        transformOrigin: 'center center',
        transition: dragging ? 'none' : 'transform 80ms ease-out',
        cursor: canPan ? (dragging ? 'grabbing' : 'grab') : 'default',
        touchAction: canPan ? 'none' : undefined,
      }}
    />
  );
}

interface PreviewContentProps {
  type: PreviewType;
  url: string;
  name: string;
  textContent: string | null;
  zoom: number;
  rotation: number;
  onDownload: () => void;
}

function PreviewContent({
  type,
  url,
  name,
  textContent,
  zoom,
  rotation,
  onDownload,
}: PreviewContentProps) {
  switch (type) {
    case 'image':
      return <ImagePreview key={url} url={url} name={name} zoom={zoom} rotation={rotation} />;
    case 'video':
      return (
        <video src={url} controls className="max-h-full max-w-full">
          Your browser does not support the video tag.
        </video>
      );
    case 'audio':
      return (
        <div className="flex flex-col items-center gap-4">
          <FileIcon className="h-24 w-24 text-muted-foreground" />
          <p className="text-base font-medium text-foreground">{name}</p>
          <audio src={url} controls className="w-full max-w-md">
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    case 'pdf':
      return <iframe src={url} title={name} className="h-full w-full border-0" />;
    case 'text':
      return (
        <div className="h-full w-full max-w-4xl overflow-auto">
          <pre className="whitespace-pre-wrap rounded-lg bg-muted p-4 font-mono text-sm text-foreground">
            {textContent ?? ''}
          </pre>
        </div>
      );
    case 'unsupported':
    default:
      return <UnsupportedPreviewFallback name={name} onDownload={onDownload} />;
  }
}

function UnsupportedPreviewFallback({
  name,
  error,
  onDownload,
}: {
  name: string;
  error?: string;
  onDownload: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 text-center text-sm text-muted-foreground">
      {error ? (
        <AlertCircle className="h-10 w-10 text-destructive" />
      ) : (
        <FileIcon className="h-16 w-16 text-muted-foreground" />
      )}
      <p className="text-base font-medium text-foreground">{name}</p>
      <p>{error ? 'Failed to load preview' : 'Preview not available for this file type.'}</p>
      {error && <p className="text-xs">{error}</p>}
      <button
        type="button"
        onClick={onDownload}
        className="mt-1 inline-flex items-center gap-2 rounded border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted/40"
      >
        <DownloadIcon className="h-4 w-4" />
        Download
      </button>
    </div>
  );
}
